import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	UserRoles,
	TLanguageIDs,
	IGetModifierInfoV2,
	IGetModifierRestaurantProperties,
	IGetModifierRestaurantPropertiesArguments,
	IGetModifierRestaurantPortions,
	IGetModifierRestaurantPortionsArguments,
} from 'types';
import { vieModifierInitialState, ViewModifierContext, TTypeOfModalWindow } from './viewModifierContext';
import { viewModifierReducer } from './viewModifierReducer';
import { ViewModifierActionTypes } from './viewModifierActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierInfoV2, getModifierRestaurantProperties, getModifierRestaurantPortions } from 'backend/modifiers';
import { useRoleAccess } from 'hooks/useRoleAccess';

export const ViewModifierState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewModifierReducer, vieModifierInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const modifierID = params.get('modifierID');

	const {
		response: modifierFullInfoResponse,
		loading: modifierFullInfoLoading,
		refetch: modifierFullInfoRefetch,
	} = useFetchData<IGetModifierInfoV2, string>(modifierID ? getModifierInfoV2 : null, modifierID || '');

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetModifierRestaurantProperties, IGetModifierRestaurantPropertiesArguments>(
		state.restaurantIdForRestaurantProps && state.modifierFullInfo?.id ? getModifierRestaurantProperties : null,
		{
			restaurantId: state.restaurantIdForRestaurantProps || '',
			modifierId: state.modifierFullInfo?.id || '',
		},
	);

	const {
		response: restaurantPortionsResponse,
		loading: restaurantPortionsLoading,
		refetch: restaurantPortionsRefetch,
	} = useFetchData<IGetModifierRestaurantPortions, IGetModifierRestaurantPortionsArguments>(
		state.restaurantIdForPortions && state.modifierFullInfo?.id ? getModifierRestaurantPortions : null,
		{
			restaurantId: state.restaurantIdForPortions || '',
			modifierId: state.modifierFullInfo?.id || '',
		},
	);

	useNamedHeader(modifierFullInfoResponse?.data.name);

	useEffect(() => {
		modifierFullInfoLoading || restaurantPropertiesLoading || restaurantPortionsLoading
			? showLoader()
			: hideLoader();
	}, [modifierFullInfoLoading, restaurantPropertiesLoading, restaurantPortionsLoading]);

	useEffect(() => {
		if (modifierFullInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const accessibleRestaurants = hasAccess
				? modifierFullInfoResponse.data.restaurants
				: modifierFullInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			const accessiblePortionsRestaurants = hasAccess
				? modifierFullInfoResponse.data.restaurantsWithPortions.map((restaurant) => ({
						id: restaurant.id,
						name: restaurant.name || '',
				  }))
				: modifierFullInfoResponse.data.restaurantsWithPortions
						.filter((restaurant) => userRestaurantIDs.includes(restaurant.id))
						.map((restaurant) => ({
							id: restaurant.id,
							name: restaurant.name || '',
						}));

			dispatch({
				type: ViewModifierActionTypes.SET_MODIFIER_FULL_INFO,
				payload: {
					modifierFullInfo: modifierFullInfoResponse.data,
					accessibleRestaurants,
					accessiblePortionsRestaurants,
				},
			});
		}
	}, [loggedUser.restaurants, hasAccess, modifierFullInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: ViewModifierActionTypes.SET_RESTAURANT_PROPS,
				payload: {
					restaurantProps: restaurantPropertiesResponse.data,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (restaurantPortionsResponse) {
			dispatch({
				type: ViewModifierActionTypes.SET_RESTAURANT_PORTIONS,
				payload: {
					restaurantPortions: restaurantPortionsResponse.data,
				},
			});
		}
	}, [restaurantPortionsResponse]);

	useEffect(() => {
		if (state.needRefetchData) {
			modifierFullInfoRefetch();
			dispatch({
				type: ViewModifierActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.restaurantIdForRestaurantProps]);

	useEffect(() => {
		restaurantPortionsRefetch();
	}, [state.restaurantIdForPortions]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: ViewModifierActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		if (state.needRefetchRestaurantPortionsData) {
			restaurantPortionsRefetch();
			dispatch({
				type: ViewModifierActionTypes.REFETCH_PORTIONS_DATA,
				payload: {
					needRefetchRestaurantPortionsData: false,
				},
			});
		}
	}, [state.needRefetchRestaurantPortionsData]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: ViewModifierActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewModifierActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const refetchRestaurantPortionsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewModifierActionTypes.REFETCH_PORTIONS_DATA,
			payload: {
				needRefetchRestaurantPortionsData: value,
			},
		});
	}, []);

	const setRestaurantIdForRestaurantProps = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewModifierActionTypes.SET_RESTAURANT_ID_FOR_RESTAURANT_PROPS,
				payload: {
					restaurantIdForRestaurantProps: value,
					restaurantProps: value ? state.restaurantProps : null,
				},
			});
		},
		[state.restaurantProps],
	);

	const setRestaurantIdForPortions = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewModifierActionTypes.SET_RESTAURANT_ID_FOR_PORTIONS,
				payload: {
					restaurantIdForPortions: value,
					restaurantPortions: value ? state.restaurantPortions : null,
				},
			});
		},
		[state.restaurantPortions],
	);

	const changeSelectedPortionId = useCallback((value: string) => {
		dispatch({
			type: ViewModifierActionTypes.CHANGE_SELECTED_PORTION_ID,
			payload: {
				selectedPortionId: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: ViewModifierActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ViewModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ViewModifierContext.Provider
			value={{
				refetchData,
				refetchRestPropsData,
				refetchRestaurantPortionsData,
				setRestaurantIdForRestaurantProps,
				setRestaurantIdForPortions,
				changeSelectedPortionId,
				setTranslationTab,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				needRefetchRestaurantPortionsData: state.needRefetchRestaurantPortionsData,
				modifierFullInfo: state.modifierFullInfo,
				accessibleRestaurants: state.accessibleRestaurants,
				accessiblePortionsRestaurants: state.accessiblePortionsRestaurants,
				restaurantIdForRestaurantProps: state.restaurantIdForRestaurantProps,
				restaurantIdForPortions: state.restaurantIdForPortions,
				selectedPortionId: state.selectedPortionId,
				restaurantProps: state.restaurantProps,
				restaurantPortions: state.restaurantPortions,
				selectedTranslationTab: state.selectedTranslationTab,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ViewModifierContext.Provider>
	);
};
